import { chartPointToPixel } from "../coordinateUtils";
import { createDrawingObject, replaceLastPoint } from "../shared";
import { toOverlayPrimitive } from "../overlayPrimitiveAdapter";
import { hitTestOverlayPrimitive, renderOverlayPrimitiveToCanvas } from "@vninvestcharts/engine-core";
export function calculateBarsPatternGeometry(drawing, scales, plotData) {
    if (drawing.points.length < 2 || !plotData || plotData.length === 0)
        return null;
    const [p0, p1] = drawing.points;
    const startTime = Math.min(p0.timeMsHint ?? p0.x, p1.timeMsHint ?? p1.x);
    const endTime = Math.max(p0.timeMsHint ?? p0.x, p1.timeMsHint ?? p1.x);
    // Find the pattern range in plotData
    const startIndex = plotData.findIndex(d => d.time >= startTime);
    const endIndex = plotData.findIndex(d => d.time >= endTime);
    if (startIndex === -1 || endIndex === -1 || startIndex >= endIndex)
        return null;
    const patternLength = endIndex - startIndex;
    const patternBars = plotData.slice(startIndex, endIndex + 1);
    // Normalize pattern (percentage changes)
    const patternChanges = patternBars.map((bar, i) => {
        if (i === 0)
            return 0;
        const prevClose = patternBars[i - 1].close;
        return (bar.close - prevClose) / prevClose;
    });
    // Find similar patterns
    const matches = [];
    const minSimilarity = 0.7; // 70% similarity threshold
    for (let i = 0; i <= plotData.length - patternLength - 1; i++) {
        if (i === startIndex)
            continue; // Skip the original pattern
        const candidateBars = plotData.slice(i, i + patternLength + 1);
        const candidateChanges = candidateBars.map((bar, j) => {
            if (j === 0)
                return 0;
            const prevClose = candidateBars[j - 1].close;
            return (bar.close - prevClose) / prevClose;
        });
        // Calculate correlation/similarity
        let sumPattern = 0, sumCandidate = 0, sumPatternSq = 0, sumCandidateSq = 0, sumProduct = 0;
        for (let k = 1; k < patternChanges.length; k++) {
            sumPattern += patternChanges[k];
            sumCandidate += candidateChanges[k];
            sumPatternSq += patternChanges[k] * patternChanges[k];
            sumCandidateSq += candidateChanges[k] * candidateChanges[k];
            sumProduct += patternChanges[k] * candidateChanges[k];
        }
        const n = patternChanges.length - 1;
        const numerator = n * sumProduct - sumPattern * sumCandidate;
        const denominator = Math.sqrt((n * sumPatternSq - sumPattern * sumPattern) *
            (n * sumCandidateSq - sumCandidate * sumCandidate));
        const correlation = denominator !== 0 ? numerator / denominator : 0;
        const similarity = (correlation + 1) / 2; // Convert to 0-1 range
        if (similarity >= minSimilarity) {
            const matchPixels = candidateBars.map(bar => chartPointToPixel({ x: bar.time, y: bar.close }, scales));
            matches.push({
                index: i,
                similarity,
                time: candidateBars[0].time,
                pixels: matchPixels,
            });
        }
    }
    // Sort by similarity descending
    matches.sort((a, b) => b.similarity - a.similarity);
    const patternStartPixel = chartPointToPixel({ x: patternBars[0].time, y: patternBars[0].close }, scales);
    const patternEndPixel = chartPointToPixel({ x: patternBars[patternBars.length - 1].time, y: patternBars[patternBars.length - 1].close }, scales);
    return {
        patternRange: { start: patternStartPixel, end: patternEndPixel },
        matches: matches.slice(0, 10), // Top 10 matches
    };
}
function renderBarsPattern(ctx, drawing, scales, options) {
    const primitive = toOverlayPrimitive(drawing, scales, {
        chartWidth: options.chartWidth,
        chartHeight: options.chartHeight,
        plotData: options.plotData,
    });
    if (primitive) {
        renderOverlayPrimitiveToCanvas(ctx, primitive, options);
    }
}
function hitTestBarsPattern(drawing, point, scales, tolerance, plotData) {
    const primitive = toOverlayPrimitive(drawing, scales, { plotData });
    if (!primitive)
        return false;
    return hitTestOverlayPrimitive(primitive, point.pixelX, point.pixelY, { tolerance, chartWidth: 800, chartHeight: 600 });
}
const BarsPattern = {
    name: "barsPattern",
    createDraft: (startPoint) => createDrawingObject("barsPattern", [startPoint, startPoint], {
        style: { stroke: "#00E676", strokeWidth: 1 },
    }),
    updateDraft: (draft, nextPoint) => replaceLastPoint(draft, nextPoint),
    render: () => undefined,
    hitTest: hitTestBarsPattern,
};
export default BarsPattern;
